<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">FAQ</span>
            </nav>
            <h1 class="page-title">Frequently Asked Questions</h1>
            <p class="page-subtitle">Find answers to common questions about county services and payments</p>
          </div>
        </div>
      </section>

      <!-- Search Section -->
      <section class="search-section">
        <div class="container">
          <div class="search-card">
            <div class="search-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
            <input 
              type="text" 
              v-model="searchQuery" 
              class="search-input" 
              placeholder="Search for answers..."
              @input="filterFAQs"
            />
            <span v-if="searchQuery" class="search-clear" @click="clearSearch">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </span>
          </div>
        </div>
      </section>

      <!-- FAQ Content -->
      <section class="faq-section">
        <div class="container">
          <div class="faq-layout">
            <!-- Categories Sidebar -->
            <aside class="categories-sidebar">
              <h3>Categories</h3>
              <nav class="category-nav">
                <button 
                  v-for="category in categories" 
                  :key="category.id"
                  class="category-btn"
                  :class="{ active: activeCategory === category.id }"
                  @click="setCategory(category.id)"
                >
                  <span class="category-icon" v-html="category.icon"></span>
                  <span class="category-name">{{ category.name }}</span>
                  <span class="category-count">{{ getCategoryCount(category.id) }}</span>
                </button>
              </nav>

              <!-- Quick Contact -->
              <div class="quick-contact">
                <h4>Still have questions?</h4>
                <p>Can't find what you're looking for? Contact our support team.</p>
                <a href="/contact" class="contact-link">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  Contact Support
                </a>
              </div>
            </aside>

            <!-- FAQ List -->
            <div class="faq-content">
              <!-- Category Header -->
              <div class="category-header">
                <h2>{{ currentCategoryName }}</h2>
                <span class="results-count">{{ filteredFAQs.length }} {{ filteredFAQs.length === 1 ? 'question' : 'questions' }}</span>
              </div>

              <!-- No Results -->
              <div v-if="filteredFAQs.length === 0" class="no-results">
                <div class="no-results-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </div>
                <h3>No results found</h3>
                <p>Try adjusting your search or browse by category</p>
                <button class="btn btn-secondary" @click="clearSearch">Clear Search</button>
              </div>

              <!-- FAQ Accordion -->
              <div class="faq-list">
                <div 
                  v-for="(faq, index) in filteredFAQs" 
                  :key="index" 
                  class="faq-item"
                  :class="{ open: openFAQ === index }"
                >
                  <button class="faq-question" @click="toggleFAQ(index)">
                    <span class="question-text">{{ faq.question }}</span>
                    <span class="toggle-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline v-if="openFAQ !== index" points="6 9 12 15 18 9"></polyline>
                        <polyline v-else points="18 15 12 9 6 15"></polyline>
                      </svg>
                    </span>
                  </button>
                  <transition name="accordion">
                    <div v-if="openFAQ === index" class="faq-answer">
                      <div class="answer-content" v-html="faq.answer"></div>
                      <div v-if="faq.links && faq.links.length" class="answer-links">
                        <span>Related:</span>
                        <a v-for="link in faq.links" :key="link.url" :href="link.url">{{ link.text }}</a>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Help Section -->
      <section class="help-section">
        <div class="container">
          <div class="help-grid">
            <div class="help-card">
              <div class="help-icon blue">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
              </div>
              <h3>Call Us</h3>
              <p>Speak with our support team</p>
              <a href="tel:+254700000000" class="help-link">+254 700 000 000</a>
            </div>

            <div class="help-card">
              <div class="help-icon green">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <h3>Email Us</h3>
              <p>We'll respond within 24 hours</p>
              <a href="mailto:support@county.go.ke" class="help-link">support@county.go.ke</a>
            </div>

            <div class="help-card">
              <div class="help-icon purple">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
              <h3>Visit Us</h3>
              <p>Come to our offices</p>
              <a href="/contact" class="help-link">Find Locations</a>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: 'FAQ',
  components: { HeaderComponent, FooterComponent },

  data() {
    return {
      searchQuery: '',
      activeCategory: 'all',
      openFAQ: null,
      categories: [
        { 
          id: 'all', 
          name: 'All Questions',
          icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>'
        },
        { 
          id: 'payments', 
          name: 'Payments',
          icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>'
        },
        { 
          id: 'business', 
          name: 'Business Permits',
          icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>'
        },
        { 
          id: 'land', 
          name: 'Land Rates',
          icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>'
        },
        { 
          id: 'parking', 
          name: 'Parking',
          icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="3" width="15" height="13" rx="2"></rect><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="12.5" cy="18.5" r="2.5"></circle></svg>'
        },
        { 
          id: 'receipts', 
          name: 'Receipts',
          icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>'
        },
        { 
          id: 'account', 
          name: 'Account',
          icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>'
        }
      ],
      faqs: [
        // Payments
        {
          category: 'payments',
          question: 'How do I pay my county bills using M-Pesa?',
          answer: `
            <p>To pay your county bills via M-Pesa:</p>
            <ol>
              <li>Go to M-Pesa on your phone</li>
              <li>Select <strong>Lipa na M-Pesa</strong></li>
              <li>Select <strong>Pay Bill</strong></li>
              <li>Enter the <strong>Business Number</strong> (Paybill number)</li>
              <li>Enter your <strong>Bill/Invoice Number</strong> as the Account Number</li>
              <li>Enter the <strong>Amount</strong></li>
              <li>Enter your <strong>M-Pesa PIN</strong> and confirm</li>
            </ol>
            <p>You will receive an SMS confirmation once the payment is successful.</p>
          `,
          links: [
            { text: 'Pay Bill', url: '/#pay-bill' },
            { text: 'View Invoice', url: '/#payment' }
          ]
        },
        {
          category: 'payments',
          question: 'What is the M-Pesa Paybill number for county payments?',
          answer: `
            <p>The M-Pesa Paybill number varies by county. You can find your county's Paybill number:</p>
            <ul>
              <li>On your invoice or bill document</li>
              <li>On the payment page of this portal</li>
              <li>By contacting the county revenue office</li>
            </ul>
            <p>Always verify the Paybill number before making a payment to ensure it goes to the correct account.</p>
          `,
          links: [
            { text: 'Payment Portal', url: '/#payment' }
          ]
        },
        {
          category: 'payments',
          question: 'Can I make partial payments?',
          answer: `
            <p>Yes, partial payments are accepted for most county services. The remaining balance will be reflected in your account and can be paid at a later date.</p>
            <p><strong>Note:</strong> Some services may require full payment before they can be processed. Check the specific service requirements or contact the relevant department.</p>
          `
        },
        {
          category: 'payments',
          question: 'How long does it take for my payment to reflect?',
          answer: `
            <p>M-Pesa payments typically reflect in our system within <strong>1-5 minutes</strong>. However, during peak times or system maintenance, it may take up to <strong>24 hours</strong>.</p>
            <p>If your payment hasn't reflected after 24 hours:</p>
            <ul>
              <li>Check your M-Pesa confirmation SMS</li>
              <li>Verify the Paybill and Account numbers used</li>
              <li>Contact our support team with your transaction details</li>
            </ul>
          `,
          links: [
            { text: 'Check Payment Status', url: '/#payment' }
          ]
        },
        {
          category: 'payments',
          question: 'What happens if I pay the wrong amount?',
          answer: `
            <p>If you underpay, the partial payment will be credited to your account, and you'll need to pay the remaining balance.</p>
            <p>If you overpay, the excess amount will be credited to your account and can be applied to future payments. For refunds, please contact the county revenue office with your payment details.</p>
          `
        },
        // Business Permits
        {
          category: 'business',
          question: 'How do I apply for a new business permit?',
          answer: `
            <p>To apply for a new business permit:</p>
            <ol>
              <li>Visit the <strong>Business Registration</strong> page</li>
              <li>Fill in your business details (name, type, location)</li>
              <li>Select the applicable fees and charges</li>
              <li>Generate and pay the invoice</li>
              <li>Once payment is confirmed, your permit will be processed</li>
            </ol>
            <p>Processing typically takes 1-3 business days after payment confirmation.</p>
          `,
          links: [
            { text: 'Register Business', url: '/#create-business' },
            { text: 'Print Permit', url: '/#print-permit' }
          ]
        },
        {
          category: 'business',
          question: 'How do I renew my business permit?',
          answer: `
            <p>Business permits must be renewed annually. To renew:</p>
            <ol>
              <li>Go to the <strong>Pay Bill</strong> page</li>
              <li>Enter your Business ID or previous permit number</li>
              <li>Pay the renewal fee</li>
              <li>Print your updated permit</li>
            </ol>
            <p>We recommend renewing before your permit expires to avoid penalties.</p>
          `,
          links: [
            { text: 'Pay Bill', url: '/#pay-bill' }
          ]
        },
        {
          category: 'business',
          question: 'What documents do I need for a business permit?',
          answer: `
            <p>Required documents vary by business type but typically include:</p>
            <ul>
              <li>National ID or Passport</li>
              <li>KRA PIN Certificate</li>
              <li>Business Registration Certificate (for registered businesses)</li>
              <li>Lease agreement or proof of business premises</li>
              <li>Health certificate (for food-related businesses)</li>
              <li>Fire safety certificate (where applicable)</li>
            </ul>
            <p>Contact the licensing department for specific requirements for your business type.</p>
          `
        },
        {
          category: 'business',
          question: 'How do I print my business permit?',
          answer: `
            <p>To print your business permit:</p>
            <ol>
              <li>Go to the <strong>Print Permit</strong> page</li>
              <li>Enter your Business ID or search by business name</li>
              <li>Verify the permit details</li>
              <li>Click <strong>Print Permit</strong></li>
            </ol>
            <p>Make sure your payment is confirmed before attempting to print. Only fully paid permits can be printed.</p>
          `,
          links: [
            { text: 'Print Permit', url: '/#print-permit' }
          ]
        },
        // Land Rates
        {
          category: 'land',
          question: 'How do I check my land rates balance?',
          answer: `
            <p>To check your land rates balance:</p>
            <ol>
              <li>Go to the <strong>Land Search</strong> page</li>
              <li>Enter your LR Number, owner name, or ID number</li>
              <li>View your property details and outstanding balance</li>
            </ol>
            <p>You can also pay directly from the search results page.</p>
          `,
          links: [
            { text: 'Land Search', url: '/#land-search' },
            { text: 'Land Rates', url: '/#land-rates' }
          ]
        },
        {
          category: 'land',
          question: 'When are land rates due?',
          answer: `
            <p>Land rates are typically due annually. The specific due date varies by county but is usually at the beginning of the financial year (July).</p>
            <p>Late payments may attract <strong>penalties and interest charges</strong>. We recommend paying on time to avoid additional costs.</p>
          `
        },
        {
          category: 'land',
          question: 'How do I get a land rates clearance certificate?',
          answer: `
            <p>A land rates clearance certificate is issued once all outstanding rates are paid. To obtain one:</p>
            <ol>
              <li>Clear all outstanding land rates for your property</li>
              <li>Visit the county lands office with your payment receipts</li>
              <li>Request for a clearance certificate</li>
            </ol>
            <p>Processing typically takes 2-5 business days.</p>
          `
        },
        // Parking
        {
          category: 'parking',
          question: 'How do I pay for daily parking?',
          answer: `
            <p>To pay for daily parking:</p>
            <ol>
              <li>Go to the <strong>Daily Parking</strong> page</li>
              <li>Enter your vehicle number plate</li>
              <li>Select the parking zone and duration</li>
              <li>Pay via M-Pesa</li>
            </ol>
            <p>You can also pay via USSD or the county mobile app if available.</p>
          `,
          links: [
            { text: 'Daily Parking', url: '/#daily-parking' }
          ]
        },
        {
          category: 'parking',
          question: 'What are the parking rates?',
          answer: `
            <p>Parking rates vary by zone and vehicle type:</p>
            <ul>
              <li><strong>CBD/Town Center:</strong> Higher rates</li>
              <li><strong>Suburban areas:</strong> Lower rates</li>
              <li><strong>Motorcycles:</strong> Reduced rates</li>
              <li><strong>PSV vehicles:</strong> Special rates apply</li>
            </ul>
            <p>Check the Daily Parking page for current rates in your area.</p>
          `,
          links: [
            { text: 'View Parking Rates', url: '/#daily-parking' }
          ]
        },
        {
          category: 'parking',
          question: 'Can I apply for a seasonal parking sticker?',
          answer: `
            <p>Yes, seasonal parking stickers are available for regular parkers. Benefits include:</p>
            <ul>
              <li>Discounted rates compared to daily parking</li>
              <li>Convenience of not paying daily</li>
              <li>Valid for a specified period (monthly, quarterly, or annually)</li>
            </ul>
            <p>Visit the county revenue office or contact support to apply for a seasonal sticker.</p>
          `
        },
        // Receipts
        {
          category: 'receipts',
          question: 'How do I print my payment receipt?',
          answer: `
            <p>To print a receipt:</p>
            <ol>
              <li>Go to the <strong>Print Receipt</strong> page</li>
              <li>Enter your transaction code or bill number</li>
              <li>Click <strong>Search</strong></li>
              <li>Verify the receipt details and click <strong>Print</strong></li>
            </ol>
            <p>You can print receipts for any payment made through the portal.</p>
          `,
          links: [
            { text: 'Print Receipt', url: '/#print-receipt' }
          ]
        },
        {
          category: 'receipts',
          question: 'I lost my receipt. Can I get a copy?',
          answer: `
            <p>Yes, you can reprint receipts at any time. You'll need either:</p>
            <ul>
              <li>The M-Pesa transaction code</li>
              <li>The bill/invoice number</li>
              <li>Your phone number (used for payment)</li>
            </ul>
            <p>Go to the Print Receipt page and search using any of these details.</p>
          `,
          links: [
            { text: 'Print Receipt', url: '/#print-receipt' }
          ]
        },
        {
          category: 'receipts',
          question: 'Are digital receipts legally valid?',
          answer: `
            <p>Yes, digital receipts generated from this portal are <strong>legally valid</strong> official county government receipts. They contain:</p>
            <ul>
              <li>Unique receipt number</li>
              <li>Transaction details and timestamp</li>
              <li>Official county seal/logo</li>
              <li>QR code for verification</li>
            </ul>
            <p>These receipts can be used for any official purpose including tax filings and audits.</p>
          `
        },
        // Account
        {
          category: 'account',
          question: 'Do I need to create an account to make payments?',
          answer: `
            <p>No, you don't need to create an account to make payments. The portal allows guest payments where you simply:</p>
            <ul>
              <li>Enter your bill/invoice number</li>
              <li>Pay via M-Pesa</li>
              <li>Receive confirmation</li>
            </ul>
            <p>However, having an account allows you to track payment history and manage multiple bills easily.</p>
          `
        },
        {
          category: 'account',
          question: 'How do I check my payment history?',
          answer: `
            <p>To check your payment history:</p>
            <ol>
              <li>Go to the <strong>Payment</strong> page</li>
              <li>Use the <strong>Check Status</strong> tab</li>
              <li>Enter your bill number or transaction code</li>
              <li>View your payment details and status</li>
            </ol>
          `,
          links: [
            { text: 'Payment Portal', url: '/#payment' }
          ]
        },
        {
          category: 'account',
          question: 'How do I update my contact information?',
          answer: `
            <p>To update your contact information (phone, email, address), please:</p>
            <ul>
              <li>Visit the relevant county department (Business Licensing, Land Office, etc.)</li>
              <li>Bring your ID and any relevant documents</li>
              <li>Request an update to your records</li>
            </ul>
            <p>For business permits, you may need to provide additional documentation for major changes.</p>
          `
        }
      ]
    };
  },

  computed: {
    filteredFAQs() {
      let results = this.faqs;

      // Filter by category
      if (this.activeCategory !== 'all') {
        results = results.filter(faq => faq.category === this.activeCategory);
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        results = results.filter(faq => 
          faq.question.toLowerCase().includes(query) ||
          faq.answer.toLowerCase().includes(query)
        );
      }

      return results;
    },

    currentCategoryName() {
      const category = this.categories.find(c => c.id === this.activeCategory);
      return category ? category.name : 'All Questions';
    }
  },

  methods: {
    setCategory(categoryId) {
      this.activeCategory = categoryId;
      this.openFAQ = null;
    },

    toggleFAQ(index) {
      this.openFAQ = this.openFAQ === index ? null : index;
    },

    getCategoryCount(categoryId) {
      if (categoryId === 'all') return this.faqs.length;
      return this.faqs.filter(faq => faq.category === categoryId).length;
    },

    filterFAQs() {
      this.openFAQ = null;
    },

    clearSearch() {
      this.searchQuery = '';
      this.openFAQ = null;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;700&display=swap');

.page-wrapper { font-family: 'Plus Jakarta Sans', sans-serif; background: #f5f7fa; min-height: 100vh; }
.main-content { padding-bottom: 80px; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

/* Hero */
.page-hero { background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 50%, #0a3459 100%); padding: 48px 0 80px; position: relative; overflow: hidden; }
.page-hero::before { content: ''; position: absolute; top: 0; right: 0; width: 500px; height: 500px; background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%); transform: translate(30%, -30%); }
.hero-content { text-align: center; position: relative; z-index: 1; }

.breadcrumb-nav { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 16px; }
.breadcrumb-link { color: rgba(255,255,255,0.7); text-decoration: none; font-size: 14px; transition: color 0.2s; }
.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; font-size: 14px; }

.page-title { font-size: 36px; font-weight: 800; color: white; margin: 0 0 12px; letter-spacing: -0.5px; }
.page-subtitle { font-size: 16px; color: rgba(255,255,255,0.8); margin: 0; }

/* Search Section */
.search-section { margin-top: -40px; position: relative; z-index: 10; margin-bottom: 40px; }
.search-card { max-width: 600px; margin: 0 auto; background: white; border-radius: 16px; box-shadow: 0 10px 40px rgba(0,0,0,0.15); display: flex; align-items: center; padding: 8px 16px; gap: 12px; }
.search-icon { color: #94a3b8; flex-shrink: 0; }
.search-input { flex: 1; border: none; outline: none; font-size: 16px; font-family: inherit; padding: 14px 0; background: transparent; }
.search-input::placeholder { color: #94a3b8; }
.search-clear { width: 32px; height: 32px; background: #f1f5f9; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #64748b; cursor: pointer; transition: all 0.2s; flex-shrink: 0; }
.search-clear:hover { background: #e2e8f0; color: #1e293b; }

/* FAQ Section */
.faq-section { padding: 20px 0; }
.faq-layout { display: grid; grid-template-columns: 280px 1fr; gap: 40px; align-items: start; }

/* Categories Sidebar */
.categories-sidebar { position: sticky; top: 100px; }
.categories-sidebar h3 { font-size: 13px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 16px; padding: 0 12px; }
.category-nav { display: flex; flex-direction: column; gap: 4px; margin-bottom: 32px; }
.category-btn { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: transparent; border: none; border-radius: 10px; font-family: inherit; font-size: 14px; font-weight: 500; color: #475569; cursor: pointer; transition: all 0.2s; text-align: left; width: 100%; }
.category-btn:hover { background: #f1f5f9; color: #1e293b; }
.category-btn.active { background: #0f4c81; color: white; }
.category-icon { display: flex; align-items: center; justify-content: center; width: 24px; flex-shrink: 0; }
.category-name { flex: 1; }
.category-count { font-size: 12px; font-weight: 600; background: rgba(0,0,0,0.1); padding: 2px 8px; border-radius: 10px; }
.category-btn.active .category-count { background: rgba(255,255,255,0.2); }

/* Quick Contact */
.quick-contact { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border-radius: 14px; padding: 24px; }
.quick-contact h4 { font-size: 16px; font-weight: 700; color: #1e3a5f; margin: 0 0 8px; }
.quick-contact p { font-size: 13px; color: #475569; margin: 0 0 16px; line-height: 1.5; }
.contact-link { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #0f4c81; text-decoration: none; }
.contact-link:hover { text-decoration: underline; }

/* FAQ Content */
.faq-content { }
.category-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #e2e8f0; }
.category-header h2 { font-size: 24px; font-weight: 800; color: #1e293b; margin: 0; }
.results-count { font-size: 14px; color: #64748b; background: #f1f5f9; padding: 6px 14px; border-radius: 20px; }

/* No Results */
.no-results { text-align: center; padding: 60px 20px; background: white; border-radius: 16px; }
.no-results-icon { width: 100px; height: 100px; background: #f1f5f9; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; color: #94a3b8; }
.no-results h3 { font-size: 20px; font-weight: 700; color: #1e293b; margin: 0 0 8px; }
.no-results p { font-size: 14px; color: #64748b; margin: 0 0 24px; }

/* FAQ List */
.faq-list { display: flex; flex-direction: column; gap: 12px; }
.faq-item { background: white; border-radius: 14px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.06); border: 1px solid #e2e8f0; transition: all 0.2s; }
.faq-item:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
.faq-item.open { border-color: #0f4c81; box-shadow: 0 4px 20px rgba(15, 76, 129, 0.15); }

.faq-question { display: flex; align-items: center; justify-content: space-between; gap: 16px; width: 100%; padding: 20px 24px; background: transparent; border: none; font-family: inherit; font-size: 16px; font-weight: 600; color: #1e293b; cursor: pointer; text-align: left; transition: all 0.2s; }
.faq-question:hover { background: #f8fafc; }
.faq-item.open .faq-question { background: #f0f9ff; color: #0f4c81; }
.question-text { flex: 1; line-height: 1.4; }
.toggle-icon { width: 32px; height: 32px; background: #f1f5f9; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #64748b; flex-shrink: 0; transition: all 0.2s; }
.faq-item.open .toggle-icon { background: #0f4c81; color: white; }

.faq-answer { padding: 0 24px 24px; }
.answer-content { color: #475569; font-size: 15px; line-height: 1.7; }
.answer-content p { margin: 0 0 12px; }
.answer-content p:last-child { margin-bottom: 0; }
.answer-content ul, .answer-content ol { margin: 12px 0; padding-left: 24px; }
.answer-content li { margin-bottom: 8px; }
.answer-content strong { color: #1e293b; }

.answer-links { display: flex; align-items: center; gap: 12px; margin-top: 16px; padding-top: 16px; border-top: 1px solid #e2e8f0; flex-wrap: wrap; }
.answer-links span { font-size: 13px; color: #64748b; }
.answer-links a { font-size: 13px; font-weight: 600; color: #0f4c81; text-decoration: none; padding: 6px 12px; background: #eff6ff; border-radius: 6px; transition: all 0.2s; }
.answer-links a:hover { background: #0f4c81; color: white; }

/* Accordion Transition */
.accordion-enter-active, .accordion-leave-active { transition: all 0.3s ease; overflow: hidden; }
.accordion-enter-from, .accordion-leave-to { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; }
.accordion-enter-to, .accordion-leave-from { opacity: 1; max-height: 500px; }

/* Help Section */
.help-section { padding: 60px 0 0; }
.help-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.help-card { background: white; border-radius: 16px; padding: 32px; text-align: center; box-shadow: 0 4px 15px rgba(0,0,0,0.08); transition: all 0.3s; }
.help-card:hover { transform: translateY(-4px); box-shadow: 0 8px 30px rgba(0,0,0,0.12); }
.help-icon { width: 72px; height: 72px; border-radius: 16px; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; }
.help-icon.blue { background: #dbeafe; color: #2563eb; }
.help-icon.green { background: #d1fae5; color: #059669; }
.help-icon.purple { background: #ede9fe; color: #7c3aed; }
.help-card h3 { font-size: 18px; font-weight: 700; color: #1e293b; margin: 0 0 8px; }
.help-card p { font-size: 14px; color: #64748b; margin: 0 0 16px; }
.help-link { font-size: 15px; font-weight: 600; color: #0f4c81; text-decoration: none; }
.help-link:hover { text-decoration: underline; }

/* Buttons */
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 10px; padding: 12px 24px; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; border: none; font-family: inherit; }
.btn-secondary { background: #f1f5f9; color: #475569; }
.btn-secondary:hover { background: #e2e8f0; }

/* Responsive */
@media (max-width: 1024px) {
  .faq-layout { grid-template-columns: 1fr; }
  .categories-sidebar { position: static; display: flex; flex-direction: column; }
  .category-nav { flex-direction: row; flex-wrap: wrap; gap: 8px; }
  .category-btn { width: auto; padding: 10px 16px; }
  .quick-contact { display: none; }
}

@media (max-width: 768px) {
  .page-title { font-size: 28px; }
  .help-grid { grid-template-columns: 1fr; }
  .category-btn { padding: 8px 14px; font-size: 13px; }
  .category-count { display: none; }
  .faq-question { padding: 16px 20px; font-size: 15px; }
  .faq-answer { padding: 0 20px 20px; }
  .category-header h2 { font-size: 20px; }
}

@media (max-width: 480px) {
  .category-nav { flex-direction: column; }
  .category-btn { width: 100%; justify-content: flex-start; }
  .category-count { display: block; }
}
</style>
